/**
 * Yona messaging E2EE v1 — X25519 + ChaCha20-Poly1305 + HKDF-SHA256.
 * Must stay aligned with yona-mobile/src/crypto/messageE2ee.ts and apps/messaging/e2ee_payload.py.
 */
import { x25519 } from '@noble/curves/ed25519';
import { chacha20poly1305 } from '@noble/ciphers/chacha';
import { hkdf } from '@noble/hashes/hkdf';
import { sha256 } from '@noble/hashes/sha256';
import { utf8ToBytes, bytesToUtf8 } from '@noble/hashes/utils';

const HKDF_INFO_WRAP = utf8ToBytes('yona-msg-v1-wrap');

function randomBytes(n) {
  const u = new Uint8Array(n);
  crypto.getRandomValues(u);
  return u;
}

export function b64Encode(bytes) {
  let s = '';
  bytes.forEach((b) => {
    s += String.fromCharCode(b);
  });
  return btoa(s);
}

function b64Decode(s) {
  const bin = atob(s);
  const u = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) u[i] = bin.charCodeAt(i);
  return u;
}

function wrapContentKeyForRecipient(contentKey32, recipientPub32) {
  const ephemeralSk = x25519.utils.randomSecretKey();
  const ephPk = x25519.getPublicKey(ephemeralSk);
  const shared = x25519.getSharedSecret(ephemeralSk, recipientPub32);
  const wk = hkdf(sha256, shared, undefined, HKDF_INFO_WRAP, 32);
  const nonce = randomBytes(12);
  const wrapped = chacha20poly1305(wk, nonce).encrypt(contentKey32);
  const out = new Uint8Array(32 + 12 + wrapped.length);
  out.set(ephPk, 0);
  out.set(nonce, 32);
  out.set(wrapped, 44);
  return b64Encode(out);
}

/**
 * Sort wrap target keys "userId:deviceUuid" by numeric user id then device id string.
 * @param {string[]} keys
 * @returns {string[]}
 */
export function sortMessagingWrapTargetKeys(keys) {
  const parsed = keys.map((k) => {
    const i = k.indexOf(':');
    const uid = parseInt(k.slice(0, i), 10);
    const dev = k.slice(i + 1);
    return { k, uid, dev };
  });
  parsed.sort((a, b) => {
    if (a.uid !== b.uid) return a.uid - b.uid;
    return a.dev.localeCompare(b.dev);
  });
  return parsed.map((x) => x.k);
}

/**
 * @param {string} plaintext
 * @param {string[]} wrapTargetKeys — composite keys userId:deviceUuid (all participant devices)
 * @param {Record<string, string>} publicKeyB64ByTargetKey — composite key -> base64 X25519 pub (32 bytes)
 * @returns {object} e2ee_payload for POST /messaging/messages/
 */
export function buildE2eePayloadV1(plaintext, wrapTargetKeys, publicKeyB64ByTargetKey) {
  const mk = randomBytes(32);
  const iv = randomBytes(12);
  const ptBytes = utf8ToBytes(plaintext);
  const ct = chacha20poly1305(mk, iv).encrypt(ptBytes);
  const wraps = {};
  const sorted = sortMessagingWrapTargetKeys(wrapTargetKeys);
  for (const key of sorted) {
    const pubB64 = publicKeyB64ByTargetKey[key];
    if (!pubB64) {
      throw new Error(
        `Missing encryption public key for wrap target ${key}. They may need to open Messages once to register keys.`
      );
    }
    const pub = b64Decode(pubB64);
    if (pub.length !== 32) {
      throw new Error(`Invalid public key length for ${key}`);
    }
    wraps[key] = wrapContentKeyForRecipient(mk, pub);
  }
  return { v: 1, iv: b64Encode(iv), ct: b64Encode(ct), wraps };
}

/**
 * @param {{ iv: string, ct: string, wrap: string }|null|undefined} bundle — from API e2ee_bundle
 * @param {Uint8Array} secretKey32 — X25519 private key
 * @returns {string|null}
 */
export function decryptE2eeBundle(bundle, secretKey32) {
  if (!bundle?.iv || !bundle.ct || !bundle.wrap) return null;
  try {
    const iv = b64Decode(bundle.iv);
    const ct = b64Decode(bundle.ct);
    const wrapRaw = b64Decode(bundle.wrap);
    const ephPub = wrapRaw.slice(0, 32);
    const wNonce = wrapRaw.slice(32, 44);
    const wCt = wrapRaw.slice(44);
    const shared = x25519.getSharedSecret(secretKey32, ephPub);
    const wk = hkdf(sha256, shared, undefined, HKDF_INFO_WRAP, 32);
    const mk = chacha20poly1305(wk, wNonce).decrypt(wCt);
    const pt = chacha20poly1305(mk, iv).decrypt(ct);
    return bytesToUtf8(pt);
  } catch {
    return null;
  }
}

export function generateX25519Identity() {
  const secretKey = x25519.utils.randomSecretKey();
  const publicKey = x25519.getPublicKey(secretKey);
  return { secretKey, publicKey, publicKeyB64: b64Encode(publicKey) };
}

export function decodeSecretKeyB64(b64) {
  return b64Decode(b64);
}

export function encodeSecretKeyB64(secretKey32) {
  return b64Encode(secretKey32);
}

export function enrichMessageWithDecryptedBody(message, secretKey32) {
  if (!message || !secretKey32) return message;
  if (message.content_protection === 'user_e2ee' && message.e2ee_bundle) {
    const text = decryptE2eeBundle(message.e2ee_bundle, secretKey32);
    return { ...message, decrypted_content: text ?? '[Unable to decrypt]' };
  }
  return message;
}
